/**
 * RhythmSystem.ts — Chris's Tempo mechanic (§9.4).
 * Tempo is a global float 0.0–1.0 (default 0.5). All time-based actions are
 * multiplied by a modifier derived from tempo:
 *
 *   tempoModifier = 0.5 + (tempo × 0.5)   → 0.5× to 1.0× speed
 *
 * Four on the Floor (Chris's ability): sets tempo to 1.0 for 20s.
 * Enemy hit on Chris: −TEMPO_ENEMY_HIT per hit.
 * Natural decay: exponential approach back to TEMPO_DEFAULT over ~5s.
 *
 * ADMIN-tunable: tempoDefault | tempoDecayRate | tempoEnemyHit
 */

import { TEMPO_DEFAULT, TEMPO_DECAY_RATE, TEMPO_ENEMY_HIT } from '../constants';

export class RhythmSystem {
  tempo: number;

  /** Rate at which tempo returns to default (per frame at 60fps → /s = rate × 60). */
  private readonly decayRate: number;
  private readonly enemyHit: number;
  private readonly defaultTempo: number;

  constructor(opts?: { tempoDefault?: number; tempoDecayRate?: number; tempoEnemyHit?: number }) {
    this.defaultTempo = opts?.tempoDefault  ?? TEMPO_DEFAULT;
    this.decayRate    = opts?.tempoDecayRate ?? TEMPO_DECAY_RATE;
    this.enemyHit     = opts?.tempoEnemyHit  ?? TEMPO_ENEMY_HIT;
    this.tempo        = this.defaultTempo;
  }

  // ── Per-frame update ────────────────────────────────────────────────────

  update(delta: number): void {
    // Exponential approach toward defaultTempo.
    // decayRate is expressed per-frame at 60fps; convert to per-second.
    const ratePerSecond = this.decayRate * 60;
    this.tempo += (this.defaultTempo - this.tempo) * Math.min(1, ratePerSecond * delta);
  }

  // ── Accessors ───────────────────────────────────────────────────────────

  /** Multiplier applied to all time-based operations (move speed, build speed, etc). */
  get tempoModifier(): number {
    return 0.5 + this.tempo * 0.5;
  }

  // ── Events ──────────────────────────────────────────────────────────────

  /** Chris's Four on the Floor ability — max tempo for 20 seconds (decays naturally). */
  onFourOnTheFloor(): void {
    this.tempo = 1.0;
  }

  /** Called when an enemy scores a hit on Chris. */
  onChrisHit(): void {
    this.tempo = Math.max(0, this.tempo - this.enemyHit);
  }

  /** Called when Chris is killed — tempo collapses to zero immediately. */
  onChrisKilled(): void {
    this.tempo = 0;
  }

  setTempo(value: number): void {
    this.tempo = Math.max(0, Math.min(1, value));
  }
}
